import { useCallback, useMemo } from "react";
import {
    type ExpandedState,
    functionalUpdate,
    type OnChangeFn,
    type PaginationState,
    type SortingState,
} from "@tanstack/react-table";
import type { ClientsWorkOverviewSortValue } from "@common/types/views/clientsWorkOverviewView";
import {
    type DataGridStatus,
    toViewSorting,
    toViewSortValue,
} from "@design-system/DataGrid";
import {
    clientsWorkOverviewViewConfig,
    type ClientsWorkOverviewRow,
} from "../clientsWorkOverviewViewConfig";
import { WORK_PAGE_SIZE } from "../workQueryFilters";
import { DEFAULT_WORK_SORT } from "../workViewSort";

const SORT_COLUMNS = clientsWorkOverviewViewConfig.columns;

const LOADING_STATUS: DataGridStatus = { state: "loading" };
const READY_STATUS: DataGridStatus = { state: "ready" };
const REFETCHING_STATUS: DataGridStatus = {
    state: "ready",
    isRefetching: true,
};

const getStatus = ({
    isLoading,
    isRefetching,
    isError,
    onRetry,
}: {
    isLoading: boolean;
    isRefetching: boolean;
    isError: boolean;
    onRetry: () => void;
}): DataGridStatus => {
    if (isError) {
        return { state: "error", onRetry };
    }
    if (isLoading) {
        return LOADING_STATUS;
    }
    return isRefetching ? REFETCHING_STATUS : READY_STATUS;
};

/**
 * Expand all as each client or group with work on this page, as ViewTable
 * did: `true` would also open every row of the next page.
 */
const toExpandedParents = (rows: readonly ClientsWorkOverviewRow[]) =>
    Object.fromEntries(
        rows
            .filter((row) => row.rowType !== "work" && row.children.length > 0)
            .map((row) => [row.id, true]),
    );

interface UseWorkListServerStateParams {
    /** `null` while the page loads. */
    rows: ClientsWorkOverviewRow[] | null;
    isRefetching: boolean;
    /** The page failed to load; it shows the error state over any rows. */
    isError: boolean;
    onRetry: () => void;
    expanded: ExpandedState;
    onExpandedChange: (expanded: ExpandedState) => void;
    sort: ClientsWorkOverviewSortValue;
    onSortChange: (sort: ClientsWorkOverviewSortValue) => void;
    /** 1-based, as the server pages clients. */
    page: number;
    onPageChange: (page: number) => void;
}

/**
 * The Work list's server-owned slices in DataGrid's shape: the view's sort as
 * one sort entry, the 1-based page as a page index, expand all as an explicit
 * map, and the grid's status from loading, refetching and errors.
 */
export const useWorkListServerState = ({
    rows,
    isRefetching,
    isError,
    onRetry,
    expanded,
    onExpandedChange,
    sort,
    onSortChange,
    page,
    onPageChange,
}: UseWorkListServerStateParams) => {
    const sorting = useMemo(
        () => toViewSorting({ sort, columns: SORT_COLUMNS }),
        [sort],
    );
    const handleSortingChange = useCallback<OnChangeFn<SortingState>>(
        (updater) =>
            onSortChange(
                toViewSortValue({
                    sorting: functionalUpdate(updater, sorting),
                    columns: SORT_COLUMNS,
                }) ?? DEFAULT_WORK_SORT,
            ),
        [onSortChange, sorting],
    );
    const handleExpandedChange = useCallback<OnChangeFn<ExpandedState>>(
        (updater) => {
            const nextExpanded = functionalUpdate(updater, expanded);
            onExpandedChange(
                nextExpanded === true
                    ? toExpandedParents(rows ?? [])
                    : nextExpanded,
            );
        },
        [onExpandedChange, expanded, rows],
    );
    const pagination = useMemo(
        (): PaginationState => ({
            pageIndex: page - 1,
            pageSize: WORK_PAGE_SIZE,
        }),
        [page],
    );
    const handlePaginationChange = useCallback<OnChangeFn<PaginationState>>(
        (updater) =>
            onPageChange(functionalUpdate(updater, pagination).pageIndex + 1),
        [onPageChange, pagination],
    );

    const isLoading = rows === null;
    const status = useMemo(
        () => getStatus({ isLoading, isRefetching, isError, onRetry }),
        [isLoading, isRefetching, isError, onRetry],
    );

    return {
        status,
        sorting,
        handleSortingChange,
        handleExpandedChange,
        pagination,
        handlePaginationChange,
    };
};
